import { t } from '@framework/i18n/intl/i18next.ts';
import { type yup } from '@framework/validation/initYup.ts';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { evaluateExpression } from '../../../../expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '../../../../expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../../../../expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../../../../expressions/references/collectExpressionReferences.ts';
import { type ICreateWidgetFormSchemaInput } from '../../../IWidgetType.ts';
import { type IWidgetAdvancedOption } from '../../../Widget.ts';
import {
  createAdvancedSelectValueSchema,
  type IWidgetAdvancedOptionValue,
} from '../advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import {
  getUniqueOptionValue,
  getUniqueSelectedOptionValue,
} from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { type IAdvancedSingleSelectWidget } from './IAdvancedSingleSelectWidget.ts';

/**
 * Crea lo schema di validazione per il form del widget AdvancedSingleSelect
 */
export function createAdvancedSingleSelectFormSchema(
  input: ICreateWidgetFormSchemaInput<IAdvancedSingleSelectWidget>,
) {
  const { widget } = input;

  // Valori ammessi: stessa logica del widget (id/uniqueValue)
  const optionsMap = new Map<string, IWidgetAdvancedOptionValue>();
  for (const option of widget.options) {
    const optionId = getUniqueOptionValue(widget, option);
    optionsMap.set(optionId, option.values ?? {});
  }

  const advancedSelectObjectSchema = setAdvancedSelectDefault(
    widget,
    optionsMap,
    createAdvancedSelectValueSchema(widget),
  )
    .formBuilderFlags(FormBuilderMetaFlags.SkipDefaultValue)
    .test({
      name: 'valid-object-value',
      message: t('widgets.advanced-select.validation.invalid-option', {
        defaultValue: "L'opzione non è valida per il campo ${path}",
      }),
      test: (row: IWidgetAdvancedOptionValue) => {
        if (!row) return true;
        const optionId = getUniqueSelectedOptionValue(widget, row);
        return optionsMap.has(optionId);
      },
    })
    .notRequired()
    .transform((value, originalValue) => {
      if (!value) return value;

      // Trasformiamo l'opzione prendendo tutti i valori dalla mappa delle opzioni
      const optionId = getUniqueSelectedOptionValue(widget, value);
      if (optionId && optionsMap.has(optionId)) {
        return { ...optionsMap.get(optionId) };
      }
    });

  const schemaWithOptionConditions = setAdvancedSelectOptionEnabled(
    widget,
    advancedSelectObjectSchema,
  );

  return {
    [widget.code]: schemaWithOptionConditions,
  };
}

/**
 * Applica le condizioni di abilitazione delle singole opzioni allo schema.
 * Se l'opzione selezionata diventa disabilitata, il valore viene resettato a null.
 *
 * Stessa logica di {@link getSingleSelectWidgetSchema} adattata per le opzioni avanzate
 * che usano `values` (oggetto) invece di `value` (stringa).
 */
function setAdvancedSelectOptionEnabled(
  widget: IAdvancedSingleSelectWidget,
  schema: yup.AnySchema,
) {
  const optionConditions = getOptionsWithEnabledCondition(widget);
  if (optionConditions.length === 0) return schema;

  const references = optionConditions.flatMap(o =>
    collectExpressionReferences(o.option.enabled.expression),
  );
  const paths = references.flatMap(r => mapReferenceToPath(r));

  return schema.parentWhen(paths, (values, schema: yup.AnySchema) => {
    const variables = createEvaluationVariables(references, values);

    // Per ogni opzione, valutiamo se è abilitata
    const disabledOptionIds = new Set<string>();
    for (const { option, optionId } of optionConditions) {
      const result = evaluateExpression(option.enabled.expression, {
        variables,
      });
      if (result === false) {
        disabledOptionIds.add(optionId);
      }
    }

    return schema.withMutation((s: typeof schema) => {
      // La doppia tansform non è un problema, perché Yup le esegue in ordine.
      // In ogni caso non modifichiamo i dati, ma solo il valore restituito
      // dallo schema, quindi non c'è rischio di perdere dati.
      s.transform((value: any) => {
        if (!value) return value;
        const selectedId = getUniqueSelectedOptionValue(widget, value);
        return disabledOptionIds.has(selectedId) ? null : value;
      });
      return s;
    });
  });
}

/**
 * Imposta il valore di default per una scelta singola. Consideriamo
 * che l'utente sceglie l'ID dell'opzione, ma il valore effettivo
 * è l'oggetto completo associato a quell'ID.
 *
 * A differenza di altri campi, qui comunque dobbiamo sempre impostare
 * _almeno_ il default a `null`, altrimenti quando viene inizializzato (es.
 * `cast`) il valore del campo diventa `{}` (oggetto vuoto) e non `null`.
 */
function setAdvancedSelectDefault(
  widget: IAdvancedSingleSelectWidget,
  optionsMap: Map<string, IWidgetAdvancedOptionValue>,
  schema: yup.ObjectSchema<IWidgetAdvancedOptionValue>,
) {
  if (widget.defaultValue == null) return schema.default(null);

  const expression = widget.defaultValue;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);
      const result = evaluateExpression(expression, { variables });

      if (typeof result !== 'string' && result != null) {
        console.error(
          'AdvancedSingleSelect default value expression did not return a string ID:',
          result,
        );
        return schema.default(null);
      }

      return schema.default(
        result == null ? null : optionsMap.get(result) || null,
      );
    },
  );
}

interface IOptionWithEnabledCondition {
  option: IWidgetAdvancedOption & {
    enabled: { type: 'expression'; expression: any };
  };
  optionId: string;
}

/**
 * Ritorna solo le opzioni che hanno una condizione di abilitazione,
 * insieme al loro ID univoco.
 */
function getOptionsWithEnabledCondition(
  widget: IAdvancedSingleSelectWidget,
): IOptionWithEnabledCondition[] {
  return widget.options
    .map(option => ({
      option,
      optionId: getUniqueOptionValue(widget, option),
    }))
    .filter(
      (o): o is IOptionWithEnabledCondition =>
        o.option.enabled?.type === 'expression' &&
        !!o.option.enabled.expression,
    );
}
